import { useQueryClient } from '@tanstack/react-query'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { getImageUrl } from 'lib/image/imageUtils'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import RegenerateItemInput from 'tripPlanner/components/Itinerary/RegenerateIndividualItem/RegenerateItemInput'
import RegenerateItemTile from 'tripPlanner/components/Itinerary/RegenerateIndividualItem/RegenerateItemTile'
import type { RegenerateIndividualItemActivity } from 'tripPlanner/hooks/api/regenerateIndividualItem'
import { useAddBasicActivityToTripMutation, useRegenerateIndividualItem, useRegenerateIndividualItemMutation } from 'tripPlanner/hooks/api/regenerateIndividualItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { ReplaceItemCardSkeleton } from '../../Skeleton/ReplaceItemCardSkeleton'

const LOADING_SPARKLES_ICON_ID = 'bb71be15-c0df-4587-ba64-7b33d91fa579'

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
`

interface RegenerateItemModalProps {
  item: TripItem
}

function RegenerateItemModal({
  item,
}: RegenerateItemModalProps) {
  const { data: initialItems, isLoading: initialLoading, error: initialError } = useRegenerateIndividualItem({
    tripId: item.tripId,
    itemId: item.id,
    staleTime: Infinity,
  })
  const queryClient = useQueryClient()
  const { mutate: regenerateWithFreeText, isLoading: isRegeneratingWithFreeText, error: regenerateWithFreeTextError } = useRegenerateIndividualItemMutation()
  const { mutate: addActivity, isLoading: isAdding } = useAddBasicActivityToTripMutation({
    onSuccess: () => {
      handleClose()
    },
    onError: () => {
      handleClose()
      showSnackbar('Unable to replace the item. Please try again.', 'critical')
    },
  })
  const [freeText, setFreeText] = useState<string>('')
  const [suggestions, setSuggestions] = useState<Array<RegenerateIndividualItemActivity> | undefined>(undefined)
  const [hasUserRequestedAlternatives, setHasUserRequestedAlternatives] = useState(false)
  const [isCoolingDown, setIsCoolingDown] = useState(false)
  const latestRequestRef = useRef(0)
  const cooldownTimeoutRef = useRef<number | null>(null)
  const intl = useIntl()
  const { closeModal } = useModalCallbacks()

  const handleFreeTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFreeText(e.target.value)
  }, [setFreeText])

  const handleClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const handleRegenerateWithFreeText = useCallback(() => {
    if (isAdding || isCoolingDown) {
      return
    }
    const trimmedText = freeText.trim()
    if (!trimmedText) {
      return
    }

    const requestId = latestRequestRef.current + 1
    latestRequestRef.current = requestId

    setHasUserRequestedAlternatives(true)
    setIsCoolingDown(true)

    if (cooldownTimeoutRef.current !== null) {
      window.clearTimeout(cooldownTimeoutRef.current)
    }
    cooldownTimeoutRef.current = window.setTimeout(() => {
      setIsCoolingDown(false)
      setFreeText('')
      cooldownTimeoutRef.current = null
    }, 3000)

    regenerateWithFreeText(
      {
        tripId: item.tripId,
        itemId: item.id,
        freeText: trimmedText,
      },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(
            ['regenerateIndividualItem', item.tripId, item.id, undefined],
            data,
          )

          if (latestRequestRef.current === requestId) {
            setSuggestions(data)
          }
        },
      },
    )
  }, [cooldownTimeoutRef, freeText, isAdding, isCoolingDown, item.id, item.tripId, queryClient, regenerateWithFreeText])

  const handleSelectItem = useCallback((regenerateItem: RegenerateIndividualItemActivity) => {
    addActivity({
      tripId: item.tripId,
      itemId: item.id,
      activity: {
        basicActivity: {
          name: regenerateItem.basicActivity.name,
          placeName: regenerateItem.basicActivity.placeName,
          backUpPlaceName: regenerateItem.basicActivity.backUpPlaceName ?? null,
          secondBackUpPlaceName: regenerateItem.basicActivity.secondBackUpPlaceName ?? null,
          locationBias: regenerateItem.basicActivity.locationBias,
          description: regenerateItem.basicActivity.description,
          experienceId: regenerateItem.basicActivity.experienceId,
          date: regenerateItem.basicActivity.date ?? null,
          startTime: regenerateItem.basicActivity.startTime ?? null,
          imageUrl: regenerateItem.basicActivity.imageUrl,
          suggestReplanDay: regenerateItem.basicActivity.suggestReplanDay,
        },
        googlePlaceDetails: regenerateItem.googlePlaceDetails,
      },
    })
  }, [item.tripId, item.id, addActivity])

  useEffect(() => {
    latestRequestRef.current = 0
    setSuggestions(undefined)
    setHasUserRequestedAlternatives(false)
    setFreeText('')
    setIsCoolingDown(false)
    if (cooldownTimeoutRef.current !== null) {
      window.clearTimeout(cooldownTimeoutRef.current)
      cooldownTimeoutRef.current = null
    }
  }, [item.id])

  useEffect(() => {
    if (initialItems && latestRequestRef.current === 0) {
      setSuggestions(initialItems)
    }
  }, [initialItems])

  useEffect(() => () => {
    if (cooldownTimeoutRef.current !== null) {
      window.clearTimeout(cooldownTimeoutRef.current)
    }
  }, [])

  const isLoading = isRegeneratingWithFreeText || initialLoading || isAdding
  const isInputLoading = initialLoading || isAdding
  const alternativeItems = suggestions || initialItems
  const hasError = initialError || regenerateWithFreeTextError

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'JMIcsIvMkKdYTBkGxmX5',
          defaultMessage: 'Choose a replacement',
          description: 'Modal title for selecting a replacement trip item in the trip planner itinerary view',
        })}
        onCloseButtonClick={handleClose}
      />
      <ModalBody>
        <ModalContent flush>
          <Sheet padding="16 24" tabletPadding="24 24">
            <Group direction="vertical" gap={12} tabletGap={24} fullWidth>
              <Group direction="vertical" gap={16} verticalAlign="center" fullWidth>
                <HeadingTextBlock variant="heading5">{intl.formatMessage({
                  id: 'MJLWzOFRN2AAZgxhfPuc',
                  defaultMessage: 'Type what you’re looking for',
                  description: 'Heading prompting user to type what kind of replacement they want',
                })}
                </HeadingTextBlock>
                <RegenerateItemInput
                  freeText={freeText}
                  isLoading={isInputLoading}
                  isCoolingDown={isCoolingDown}
                  onFreeTextChange={handleFreeTextChange}
                  onSubmit={handleRegenerateWithFreeText}
                />
              </Group>

              {isLoading && <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start">
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <Image id={LOADING_SPARKLES_ICON_ID} src={getImageUrl(LOADING_SPARKLES_ICON_ID)} width={48} height={48} />
                  <HeadingTextBlock variant="heading5">{intl.formatMessage({
                    id: '9Ar_taGZ-2icVQ97BLG6',
                    defaultMessage: 'Finding the best alternatives for you...',
                    description: 'Loading message while searching for replacement trip items',
                  })}
                  </HeadingTextBlock>
                </Group>
                <Group direction="vertical" gap={12} tabletGap={16} fullWidth>
                  <ReplaceItemCardSkeleton />
                  <ReplaceItemCardSkeleton />
                  <ReplaceItemCardSkeleton />
                </Group>
              </Group>}

              {!isLoading && !!alternativeItems?.length && (
                <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start">
                  {!hasUserRequestedAlternatives && (
                    <HeadingTextBlock variant="heading5">{intl.formatMessage({
                      id: 'HFyLjTWN_db8-_mwNFjN',
                      defaultMessage: 'Pick a suggested place',
                      description: 'Heading above suggested replacement places for a trip item',
                    })}
                    </HeadingTextBlock>
                  )}
                  <Group direction="vertical" gap={12} tabletGap={16}>
                    {alternativeItems.map((regenerateItem, index) => (
                      <RegenerateItemTile
                        key={index}
                        itemToReplace={item}
                        activity={regenerateItem}
                        disabled={isLoading}
                        onClick={handleSelectItem}
                      />
                    ))}
                  </Group>
                </Group>
              )}

              {!isLoading && (!alternativeItems?.length || hasError) && (
                <Group direction="vertical" verticalAlign="center" horizontalAlign="center">
                  <BodyText variant="medium">{intl.formatMessage({
                    id: 'Lezrr4fGtaRUkqfuw1JW',
                    defaultMessage: 'No alternatives found. Please try again.',
                    description: 'Message shown when no replacement items could be found',
                  })}
                  </BodyText>
                </Group>
              )}
            </Group>
          </Sheet>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default RegenerateItemModal
